import { caAtom } from '@/atoms/caCheck';
import { loadingAtom } from '@/atoms/loadingAtom';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { CheckCaResult } from '@/types';
import { parseCA } from '@/utils/parseCA';
import { selfCheckInputs } from '@/utils/selfCheckInputs';
import { useAtom, useAtomValue } from 'jotai';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTokenSearch } from '../../hooks/useTokenSearch';

const biasedResult: CheckCaResult = {
  score: 420,
  message: "Sorry, we can't evaluate ourselves. The model might be biased.",
};

const blocklistedAddresses = [
  'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
  'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
];

export function SearchBar() {
  const ca = useAtomValue(caAtom);
  const [loading] = useAtom(loadingAtom);
  const { handleSearch } = useTokenSearch();
  const router = useRouter();

  const handleSubmit = async ({ ca }: { ca: string }) => {
    const parsedCA = parseCA(ca);
    if (parsedCA.success) {
      if (selfCheckInputs.includes(parsedCA.value.toLowerCase())) {
        return biasedResult;
      }

      const formattedCA =
        parsedCA.value.length < 10 && !parsedCA.value.startsWith('$')
          ? `$${parsedCA.value}`
          : parsedCA.value;

      await handleSearch(formattedCA);
      router.push(`/ca/${ca}`);
    }
  };

  const methods = useForm<{ ca: string }>({
    defaultValues: {
      ca: '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!ca) return;
    methods.reset({ ca });
    methods.trigger().then(isValid => {
      if (isValid) {
        handleSubmit({ ca });
      }
    });
  }, [ca]);

  const inputCa = methods.watch('ca');

  return (
    <div className="w-full max-w-md mx-auto mt-8 mb-8">
      <Form {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)} className="flex gap-3">
          <FormField
            control={methods.control}
            name="ca"
            rules={{
              validate: {
                isValidCa: value => {
                  const parsed = parseCA(value);
                  return parsed.success || parsed.error;
                },
                isBlacklisted: value => !blocklistedAddresses.includes(value),
              },
            }}
            render={({ field }) => (
              <FormItem className="space-y-0 flex-1">
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Enter valid CA"
                    disabled={loading}
                    className="flex-1 shadow-sm text-white"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <Button
            type="submit"
            id="submit-button"
            className="px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
            disabled={loading || !methods.formState.isValid}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowRight className="w-4 h-4" />
            )}
          </Button>
        </form>
      </Form>

      <br />
      {inputCa && methods.formState.errors.ca?.type === 'isValidCa' && (
        <div className="mt-2 p-2 text-sm text-yellow-200 bg-yellow-900/30 border border-yellow-600 rounded-md">
          {methods.formState.errors.ca?.message}
        </div>
      )}
      {inputCa && methods.formState.errors.ca?.type === 'isBlacklisted' && (
        <div className="mt-2 p-2 text-sm text-red-200 bg-red-900/30 border border-red-600 rounded-md">
          Currently only the Solana blockchain is supported.
        </div>
      )}
    </div>
  );
}
